import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  createMonitoringAdapter,
  monitoringEnabled,
  type Maintenance,
  type Metric,
  type MonitoringAuth,
  type MonitoringEvent,
  type Observation,
} from '../adapters/equipment-monitoring-adapter';
import './equipment-monitoring.css';
const states: Record<string, string> = {
  NORMAL: '正常',
  ABNORMAL: '异常',
  UNKNOWN: '未知',
  STALE: '数据过期',
  NOT_APPLICABLE: '不适用',
  NO_OBSERVATION: '无观测',
  RUNNING: '运行中',
  STOPPED: '已停止',
  FAULT: '故障',
  ONLINE: '在线',
  OFFLINE: '离线',
  PLANNED: '计划中',
  IN_PROGRESS: '执行中',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  RECORDED: '已记录',
  REVIEWED: '已复核',
  OPEN: '待处理',
  INVESTIGATING: '处理中',
  RESOLVED: '已处理',
  MEASURED: '有观测',
};
const kinds = { PLAN: '维护计划', CHECK: '检查记录', FAULT: '故障记录' };
const time = (s: string) => (s ? new Date(s).toLocaleString('zh-CN', { hour12: false }) : '—');
const localTime = (s = new Date().toISOString()) => {
  const d = new Date(s);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
function metric(m: Metric | undefined, unit: string) {
  return !m ? '无观测' : m.state === 'MEASURED' ? String(m.value) + unit : states[m.state];
}
function Badge({ status }: { status: string }) {
  return (
    <span className={'eqm-badge eqm-' + status.toLowerCase()}>{states[status] ?? status}</span>
  );
}
function useLoad<T>(id: string, read: (signal: AbortSignal) => Promise<T>) {
  const [value, setValue] = useState<T | null>(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [version, setVersion] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setValue(null);
    setError('');
    setBusy(true);
    read(controller.signal)
      .then(
        (v) => {
          if (!controller.signal.aborted) setValue(v);
        },
        (e) => {
          if (!controller.signal.aborted) setError(String(e instanceof Error ? e.message : e));
        },
      )
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [id, read, version]);
  return { value, error, busy, refresh: () => setVersion((v) => v + 1) };
}
function Trend({ rows }: { rows: MonitoringEvent<Observation>[] }) {
  const ordered = [...rows].reverse().slice(-20),
    points = ordered.map((e, i) => ({
      x: ordered.length === 1 ? 50 : (i * 100) / (ordered.length - 1),
      value: e.data.battery.value,
      state: e.data.battery.state,
    }));
  return (
    <section className="eqm-card">
      <h4>历史趋势</h4>
      <p className="eqm-muted">
        最近 {ordered.length} 次观测 · 按观测时间排序 · 电量趋势；未知及不适用不连接
      </p>
      {ordered.length > 0 && (
        <svg
          className="eqm-trend"
          viewBox="0 0 110 115"
          role="img"
          aria-label="实验电量历史趋势，精确数值见下方观测历史"
        >
          <line x1="5" y1="105" x2="105" y2="105" stroke="#cbd9e8" />
          {points.map((p, i) =>
            p.state === 'MEASURED' && p.value !== null ? (
              <g key={i}>
                {i > 0 && points[i - 1]!.state === 'MEASURED' && (
                  <line
                    x1={points[i - 1]!.x + 5}
                    y1={105 - points[i - 1]!.value!}
                    x2={p.x + 5}
                    y2={105 - p.value}
                    stroke="#286bab"
                    strokeWidth="1"
                  />
                )}
                <circle cx={p.x + 5} cy={105 - p.value} r="1.5" fill="#286bab">
                  <title>
                    {time(ordered[i]!.data.observedAt)}：{p.value}%
                  </title>
                </circle>
              </g>
            ) : null,
          )}
        </svg>
      )}
      <details>
        <summary>观测历史与精确数值（{rows.length}）</summary>
        <div className="eqm-scroll">
          <table>
            <caption>实验观测记录，维护处理不会改写观测</caption>
            <thead>
              <tr>
                <th>观测时间</th>
                <th>运行</th>
                <th>连接</th>
                <th>电量</th>
                <th>温度</th>
                <th>时长</th>
                <th>异常</th>
                <th>版本</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.eventId}>
                  <td>{time(e.data.observedAt)}</td>
                  <td>{states[e.data.operation]}</td>
                  <td>{states[e.data.connection]}</td>
                  <td>{metric(e.data.battery, '%')}</td>
                  <td>{metric(e.data.temperature, ' °C')}</td>
                  <td>{metric(e.data.runtimeHours, ' h')}</td>
                  <td>{e.data.anomalies || '未记录'}</td>
                  <td>v{e.asset.publishedVersion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
function ObservationForm({
  save,
  busy,
}: {
  save: (data: unknown) => Promise<boolean>;
  busy: boolean;
}) {
  const [operation, setOperation] = useState('RUNNING'),
    [connection, setConnection] = useState('ONLINE');
  const [observed, setObserved] = useState(localTime()),
    [anomalies, setAnomalies] = useState(''),
    [notes, setNotes] = useState('');
  const [fields, setFields] = useState<
    Record<'battery' | 'temperature' | 'runtimeHours', { state: string; value: string }>
  >({
    battery: { state: 'MEASURED', value: '86' },
    temperature: { state: 'MEASURED', value: '35' },
    runtimeHours: { state: 'MEASURED', value: '12' },
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    const metrics = Object.fromEntries(
      Object.entries(fields).map(([k, v]) => [
        k,
        { state: v.state, value: v.state === 'MEASURED' ? Number(v.value) : null },
      ]),
    );
    await save({
      sourceType: 'CONTROLLED_EXPERIMENT',
      observedAt: new Date(observed).toISOString(),
      operation,
      connection,
      ...metrics,
      anomalies,
      notes,
    });
  }
  return (
    <details className="eqm-card">
      <summary>录入实验观测</summary>
      <form onSubmit={(e) => void submit(e)}>
        <p className="eqm-muted">值由本机实验录入；不是设备采集。观测超过 10 分钟显示过期。</p>
        <div className="eqm-form-grid">
          <label>
            观测时间
            <input
              type="datetime-local"
              required
              value={observed}
              onChange={(e) => setObserved(e.target.value)}
            />
          </label>
          <label>
            运行状态
            <select value={operation} onChange={(e) => setOperation(e.target.value)}>
              {['RUNNING', 'STOPPED', 'FAULT', 'UNKNOWN', 'NOT_APPLICABLE'].map((s) => (
                <option key={s} value={s}>
                  {states[s]}
                </option>
              ))}
            </select>
          </label>
          <label>
            连接状态
            <select value={connection} onChange={(e) => setConnection(e.target.value)}>
              {['ONLINE', 'OFFLINE', 'UNKNOWN', 'NOT_APPLICABLE'].map((s) => (
                <option key={s} value={s}>
                  {states[s]}
                </option>
              ))}
            </select>
          </label>
          {(['battery', 'temperature', 'runtimeHours'] as const).map((key, i) => (
            <fieldset key={key}>
              <legend>{['电量 (%)', '温度 (°C)', '运行时长 (h)'][i]}</legend>
              <label>
                数据适用性
                <select
                  aria-label={['电量适用性', '温度适用性', '时长适用性'][i]}
                  value={fields[key].state}
                  onChange={(e) =>
                    setFields({ ...fields, [key]: { ...fields[key], state: e.target.value } })
                  }
                >
                  {['MEASURED', 'UNKNOWN', 'NOT_APPLICABLE'].map((s) => (
                    <option key={s} value={s}>
                      {states[s]}
                    </option>
                  ))}
                </select>
              </label>
              {fields[key].state === 'MEASURED' && (
                <label>
                  实验数值
                  <input
                    aria-label={['实验电量', '实验温度', '实验时长'][i]}
                    type="number"
                    required
                    step="any"
                    min={key === 'temperature' ? -100 : 0}
                    max={key === 'battery' ? 100 : key === 'temperature' ? 500 : 1000000}
                    value={fields[key].value}
                    onChange={(e) =>
                      setFields({ ...fields, [key]: { ...fields[key], value: e.target.value } })
                    }
                  />
                </label>
              )}
            </fieldset>
          ))}
        </div>
        <label>
          异常信息
          <textarea
            maxLength={2000}
            value={anomalies}
            onChange={(e) => setAnomalies(e.target.value)}
          />
        </label>
        <label>
          观测备注
          <textarea maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <button type="submit" disabled={busy}>
          保存实验观测
        </button>
      </form>
    </details>
  );
}
function MaintenanceForm({
  record,
  save,
  busy,
  cancel,
}: {
  record: MonitoringEvent<Maintenance> | null;
  save: (data: Maintenance) => Promise<boolean>;
  busy: boolean;
  cancel: () => void;
}) {
  const [data, setData] = useState<Maintenance>(
    record?.data ?? {
      kind: 'PLAN',
      title: '',
      status: 'PLANNED',
      scheduledAt: new Date(Date.now() + 86400000).toISOString(),
      occurredAt: '',
      details: '',
      resolution: '',
    },
  );
  const statuses =
    data.kind === 'PLAN'
      ? ['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']
      : data.kind === 'CHECK'
        ? ['RECORDED', 'REVIEWED']
        : ['OPEN', 'INVESTIGATING', 'RESOLVED'];
  return (
    <form
      className="eqm-card"
      aria-label={record ? '更新维护记录' : '新建维护记录'}
      onSubmit={(e) => {
        e.preventDefault();
        void save(data).then((ok) => {
          if (ok) cancel();
        });
      }}
    >
      <h4>{record ? '更新维护记录 · 修订 ' + record.revision : '新建维护记录'}</h4>
      <div className="eqm-form-grid">
        <label>
          记录类型
          <select
            value={data.kind}
            disabled={!!record}
            onChange={(e) => {
              const kind = e.target.value as Maintenance['kind'];
              setData({
                ...data,
                kind,
                status: kind === 'PLAN' ? 'PLANNED' : kind === 'CHECK' ? 'RECORDED' : 'OPEN',
                scheduledAt: kind === 'PLAN' ? new Date(Date.now() + 86400000).toISOString() : '',
                occurredAt: kind === 'PLAN' ? '' : new Date().toISOString(),
              });
            }}
          >
            {Object.entries(kinds).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label>
          标题
          <input
            required
            maxLength={200}
            value={data.title}
            onChange={(e) => setData({ ...data, title: e.target.value })}
          />
        </label>
        <label>
          处理状态
          <select
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                {states[s]}
              </option>
            ))}
          </select>
        </label>
        <label>
          {data.kind === 'PLAN' ? '计划时间' : '记录发生时间'}
          <input
            type="datetime-local"
            required
            value={localTime(data.kind === 'PLAN' ? data.scheduledAt : data.occurredAt)}
            onChange={(e) => {
              if (e.target.value)
                setData({
                  ...data,
                  [data.kind === 'PLAN' ? 'scheduledAt' : 'occurredAt']: new Date(
                    e.target.value,
                  ).toISOString(),
                });
            }}
          />
        </label>
      </div>
      <label>
        检查或处理内容
        <textarea
          maxLength={4000}
          value={data.details}
          onChange={(e) => setData({ ...data, details: e.target.value })}
        />
      </label>
      <label>
        处理结果
        <textarea
          maxLength={4000}
          required={['COMPLETED', 'REVIEWED', 'RESOLVED'].includes(data.status)}
          value={data.resolution}
          onChange={(e) => setData({ ...data, resolution: e.target.value })}
        />
      </label>
      <p className="eqm-muted">处理完成只记录维护进度，不自动代表装备恢复可用。</p>
      <div className="eqm-actions">
        <button type="submit" disabled={busy}>
          保存维护记录
        </button>
        <button type="button" onClick={cancel}>
          取消编辑
        </button>
      </div>
    </form>
  );
}
export function MonitoringDetail({
  auth,
  assetId,
  onChange,
}: {
  auth: MonitoringAuth;
  assetId: string;
  onChange?: () => void;
}) {
  const port = useMemo(() => createMonitoringAdapter(auth), [auth.session, auth.csrf]);
  const read = useCallback(
    (signal: AbortSignal) => port.workspace(assetId, signal),
    [port, assetId],
  );
  const resource = useLoad(assetId, read);
  const [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(''),
    [editing, setEditing] = useState<MonitoringEvent<Maintenance> | null | undefined>(undefined);
  const attempt = useRef<{ signature: string; key: string } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setEditing(undefined);
    setNotice('');
    attempt.current = null;
  }, [assetId, auth.session]);
  async function save(
    kind: 'observations' | 'maintenance',
    data: unknown,
    record?: MonitoringEvent<Maintenance>,
  ) {
    if (!resource.value || busy) return false;
    const signature = JSON.stringify({
      asset: resource.value.asset,
      kind,
      data,
      recordId: record?.recordId,
      revision: record?.revision,
    });
    if (attempt.current?.signature !== signature)
      attempt.current = { signature, key: crypto.randomUUID() };
    setBusy(true);
    setNotice('');
    try {
      await port.write(resource.value.asset, kind, data, attempt.current.key, record);
      if (!mounted.current) return false;
      attempt.current = null;
      setNotice('已保存，已重新读取服务端记录。');
      resource.refresh();
      onChange?.();
      return true;
    } catch (e) {
      if (mounted.current)
        setNotice(e instanceof Error ? e.message : '写入结果未确认，请使用相同内容重试或刷新读回');
      return false;
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  if (!monitoringEnabled()) return null;
  const w = resource.value,
    d = w?.latest?.data;
  return (
    <section className="eqm-detail" aria-label="装备状态与维护详情">
      <div className="eqm-heading">
        <h3>{w?.asset.displayName ?? '装备状态与维护'}</h3>
        <button type="button" disabled={resource.busy || busy} onClick={resource.refresh}>
          刷新装备状态
        </button>
      </div>
      {resource.busy && <p role="status">正在读取装备状态…</p>}
      {resource.error && <p role="alert">{resource.error} 当前状态已清空。</p>}
      {notice && <p role="status">{notice}</p>}
      {w && (
        <>
          <p className="eqm-muted">
            装备发布版本 v{w.asset.publishedVersion} · 实验观测 · 截至 {time(w.asOf)}
          </p>
          <div className="eqm-stats">
            <div>
              <span>观测状态</span>
              <Badge status={w.status} />
            </div>
            <div>
              <span>运行状态</span>
              <strong>{d ? states[d.operation] : '无观测'}</strong>
            </div>
            <div>
              <span>连接状态</span>
              <strong>{d ? states[d.connection] : '无观测'}</strong>
            </div>
            <div>
              <span>电量</span>
              <strong>{metric(d?.battery, '%')}</strong>
            </div>
            <div>
              <span>温度</span>
              <strong>{metric(d?.temperature, ' °C')}</strong>
            </div>
            <div>
              <span>运行时长</span>
              <strong>{metric(d?.runtimeHours, ' h')}</strong>
            </div>
          </div>
          <section className="eqm-card">
            <h4>最新观测</h4>
            {d ? (
              <>
                <p>
                  观测时间：{time(d.observedAt)} · 接收时间：{time(d.receivedAt)}
                </p>
                <p>
                  数据来源：{d.sourceLabel} · 过期时间：{time(d.expiresAt)}
                </p>
                <p>异常信息：{d.anomalies || '未记录异常'}</p>
                {d.notes && <p>{d.notes}</p>}
              </>
            ) : (
              <p>当前发布版本尚无观测；不推断设备状态。</p>
            )}
            <details>
              <summary>状态说明</summary>
              <p>
                在线仅表示实验连接状态；不等于可用、已承诺或已分配。实验异常规则：故障、离线、显式异常、电量低于20%或温度高于80°C；不构成真实设备阈值或认证。
              </p>
            </details>
          </section>
          <Trend rows={w.observations} />
          <ObservationForm save={(data) => save('observations', data)} busy={busy} />
          <section className="eqm-card">
            <div className="eqm-heading">
              <h4>维护管理</h4>
              <button type="button" onClick={() => setEditing(null)}>
                新建维护记录
              </button>
            </div>
            <p className="eqm-muted">
              {w.openFaults} 项待处理故障 · {w.overduePlans} 项逾期计划
            </p>
            {w.maintenance.length === 0 && <p>尚无维护记录。</p>}
            <div className="eqm-records">
              {w.maintenance.map((r) => (
                <article key={r.recordId}>
                  <div className="eqm-heading">
                    <strong>{r.data.title}</strong>
                    <Badge status={r.data.status} />
                  </div>
                  <p>
                    {kinds[r.data.kind]} · {time(r.data.scheduledAt || r.data.occurredAt)} · 修订{' '}
                    {r.revision}
                  </p>
                  <p>{r.data.details}</p>
                  {r.data.resolution && <p>处理结果：{r.data.resolution}</p>}
                  {r.asset.digest !== w.asset.digest ? (
                    <p>历史装备版本 v{r.asset.publishedVersion} · 保留记录</p>
                  ) : (
                    <button type="button" onClick={() => setEditing(r)}>
                      更新记录
                    </button>
                  )}
                  <details>
                    <summary>查看修改历史</summary>
                    {w.maintenanceHistory
                      .filter((h) => h.recordId === r.recordId)
                      .map((h) => (
                        <div key={h.eventId}>
                          <p>
                            修订 {h.revision} · {states[h.data.status]} · {time(h.recordedAt)}
                          </p>
                          <p>
                            {h.data.details} {h.data.resolution}
                          </p>
                        </div>
                      ))}
                  </details>
                </article>
              ))}
            </div>
          </section>
          {editing !== undefined && (
            <MaintenanceForm
              key={editing ? editing.recordId + editing.revision : 'new'}
              record={editing}
              busy={busy}
              cancel={() => setEditing(undefined)}
              save={(data) => save('maintenance', data, editing ?? undefined)}
            />
          )}
          <details className="eqm-card">
            <summary>来源与版本记录</summary>
            <pre tabIndex={0}>{JSON.stringify({ asset: w.asset, latest: w.latest }, null, 2)}</pre>
          </details>
        </>
      )}
    </section>
  );
}
export function EquipmentMonitoring({ auth }: { auth: MonitoringAuth }) {
  const port = useMemo(() => createMonitoringAdapter(auth), [auth.session, auth.csrf]);
  const read = useCallback((signal: AbortSignal) => port.overview(signal), [port]);
  const data = useLoad(auth.session, read);
  const [selected, setSelected] = useState<string | null>(null),
    [query, setQuery] = useState(''),
    [status, setStatus] = useState(''),
    [type, setType] = useState('');
  const rows = (data.value ?? []).filter(
    (w) =>
      (!query || w.asset.displayName.toLowerCase().includes(query.toLowerCase())) &&
      (!status || w.status === status) &&
      (!type || w.asset.category === type),
  );
  return (
    <section className="eqm" aria-label="装备状态监测">
      <div className="eqm-heading">
        <div>
          <h2>装备状态与维护</h2>
          <p className="eqm-muted">实验数据 · 独立观测与维护历史</p>
        </div>
        <button type="button" disabled={data.busy} onClick={data.refresh}>
          刷新状态总览
        </button>
      </div>
      <div className="eqm-filters">
        <label>
          查找装备
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="装备名称" />
        </label>
        <label>
          观测状态
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">全部状态</option>
            {['NORMAL', 'ABNORMAL', 'UNKNOWN', 'STALE', 'NOT_APPLICABLE', 'NO_OBSERVATION'].map(
              (s) => (
                <option key={s} value={s}>
                  {states[s]}
                </option>
              ),
            )}
          </select>
        </label>
        <label>
          装备类型
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">全部类型</option>
            {[...new Set((data.value ?? []).map((w) => w.asset.category))].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
      </div>
      {data.busy && <p role="status">正在读取状态总览…</p>}
      {data.error && <p role="alert">{data.error} 未使用替代数据。</p>}
      {data.value && (
        <>
          <div className="eqm-overview">
            {rows.map((w) => (
              <button
                type="button"
                key={w.asset.assetId}
                aria-pressed={selected === w.asset.assetId}
                onClick={() => setSelected(w.asset.assetId)}
              >
                <strong>{w.asset.displayName}</strong>
                <Badge status={w.status} />
                <span>
                  {w.asset.category} · v{w.asset.publishedVersion}
                </span>
                <small>
                  {w.latest ? '观测于 ' + time(w.latest.data.observedAt) : '尚无当前版本观测'}
                </small>
                <small>{w.openFaults} 项待处理故障</small>
              </button>
            ))}
          </div>
          {rows.length === 0 && <p>没有符合条件的已发布装备。</p>}
        </>
      )}
      {selected && (
        <MonitoringDetail
          key={auth.session + selected}
          auth={auth}
          assetId={selected}
          onChange={data.refresh}
        />
      )}
    </section>
  );
}
